/**
 * components/client-detail/DossierDocRow.tsx
 *
 * Sortable row for an individual file within a DossierPiece.
 * Uses @dnd-kit/sortable (useSortable) to support drag-and-drop reordering
 * within the piece's inner DndContext. Also provides up/down arrow buttons
 * (disabled at boundaries or when the piece has only one file).
 *
 * Created Apr 2026: extracted from DossierPieceRow file list to add
 * per-file reordering capability (drag handle + chevron buttons).
 * Updated Jun 2026: added inline renaming (Pencil button) and "Move to" popover
 *   (FolderInput icon) for lawyer-side document reclassification and renaming.
 * Updated Jun 2026: MovePopover rendered via React Portal to avoid overflow clipping
 *   by the dossier builder's scrollable center column.
 */

import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Z_INDEX } from '../../constants/zIndex';
import { GripVertical, ChevronUp, ChevronDown, FileText, Eye, X, Pencil, FolderInput, Check, Loader2 } from 'lucide-react';
import type { DocumentEntry } from '../../types/dossier';
import type { AvailableField } from './ClientDocumentsTab';

interface DossierDocRowProps {
  doc: DocumentEntry;
  docIndex: number;
  docsTotal: number;
  availableFields: AvailableField[];
  onPreviewDoc: (doc: DocumentEntry) => void;
  onExcludeDoc: (docId: string) => void;
  onMoveDoc: (docId: string, direction: 'up' | 'down') => void;
  onReclassify: (docId: string, targetFieldId: string) => Promise<boolean>;
  onRename: (docId: string, newName: string) => Promise<boolean>;
}

interface InlineRenameProps {
  currentName: string;
  onSave: (newName: string) => Promise<boolean>;
  onCancel: () => void;
}

function InlineRename({ currentName, onSave, onCancel }: InlineRenameProps) {
  const [value, setValue] = useState(currentName);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!value.trim() || value.trim() === currentName) { onCancel(); return; }
    setSaving(true);
    await onSave(value.trim());
    setSaving(false);
    onCancel();
  };

  return (
    <div className="flex items-center gap-1 flex-1 min-w-0">
      <input
        autoFocus
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') onCancel();
        }}
        className="flex-1 min-w-0 text-xs text-neutral-800 bg-white border border-primary-300 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-primary-200"
        disabled={saving}
      />
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="p-0.5 rounded text-success-600 hover:bg-success-50 disabled:opacity-50 transition-all"
        title="Valider"
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
      </button>
      <button
        type="button"
        onClick={onCancel}
        disabled={saving}
        className="p-0.5 rounded text-neutral-400 hover:bg-neutral-100 transition-all"
        title="Annuler"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

interface MovePopoverProps {
  doc: DocumentEntry;
  availableFields: AvailableField[];
  anchorEl: HTMLElement | null;
  onReclassify: (docId: string, targetFieldId: string) => Promise<boolean>;
  onClose: () => void;
}

function MovePopover({ doc, availableFields, anchorEl, onReclassify, onClose }: MovePopoverProps) {
  const [saving, setSaving] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: -9999, right: 0 });

  const targets = availableFields.filter(
    (f) => f.requestId === doc.requestId && f.fieldId !== doc.fieldId,
  );

  useLayoutEffect(() => {
    const compute = () => {
      if (!anchorEl) return;
      const rect = anchorEl.getBoundingClientRect();
      const popoverH = menuRef.current?.offsetHeight ?? 180;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= popoverH + 8 ? rect.bottom + 4 : rect.top - popoverH - 4;
      setPos({ top, right: window.innerWidth - rect.right });
    };

    compute();

    const handleOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        menuRef.current && !menuRef.current.contains(t) &&
        anchorEl && !anchorEl.contains(t)
      ) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [anchorEl, onClose]);

  const handleMove = async (fieldId: string) => {
    setSaving(true);
    await onReclassify(doc.id, fieldId);
    setSaving(false);
    onClose();
  };

  return createPortal(
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: Z_INDEX.POPOVER, width: '14rem' }}
      className="bg-white border border-neutral-200 rounded-xl shadow-soft-lg overflow-hidden"
    >
      {targets.length === 0 ? (
        <p className="px-3 py-3 text-xs text-neutral-400 text-center">Aucune autre catégorie disponible</p>
      ) : (
        <>
          <div className="px-3 py-2 border-b border-neutral-100">
            <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Déplacer vers</p>
          </div>
          <div className="py-1 max-h-40 overflow-y-auto">
            {targets.map((f) => (
              <button
                key={f.fieldId}
                type="button"
                disabled={saving}
                onClick={() => handleMove(f.fieldId)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-neutral-700 hover:bg-primary-50 hover:text-primary-700 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FolderInput className="w-3 h-3 shrink-0 text-neutral-400" />
                <span className="truncate">{f.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body,
  );
}

export default function DossierDocRow({
  doc,
  docIndex,
  docsTotal,
  availableFields,
  onPreviewDoc,
  onExcludeDoc,
  onMoveDoc,
  onReclassify,
  onRename,
}: DossierDocRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: doc.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? Z_INDEX.DRAG : undefined,
    opacity: isDragging ? 0.7 : 1,
  };

  const [isRenaming, setIsRenaming] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const moveButtonRef = useRef<HTMLButtonElement>(null);

  const name = doc.displayFilename ?? doc.originalFilename;

  const reclassifyTargets = availableFields.filter(
    (f) => f.requestId === doc.requestId && f.fieldId !== doc.fieldId,
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-1.5 pl-8 pr-3 py-1.5 transition-colors ${
        isDragging ? 'bg-primary-50' : 'hover:bg-neutral-50'
      }`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-neutral-300 hover:text-neutral-500 transition-colors shrink-0 touch-none opacity-40 group-hover:opacity-100 focus-visible:opacity-100"
        {...attributes}
        {...listeners}
        aria-label="Réorganiser ce fichier"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>

      <FileText className="w-3.5 h-3.5 text-success-500 shrink-0" />

      {isRenaming ? (
        <InlineRename
          currentName={name}
          onSave={(newName) => onRename(doc.id, newName)}
          onCancel={() => setIsRenaming(false)}
        />
      ) : (
        <span className="flex-1 text-xs text-neutral-500 truncate min-w-0" title={name}>
          {name}
        </span>
      )}

      {!isRenaming && (
        <div className="flex items-center gap-0.5 shrink-0">
          <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => onMoveDoc(doc.id, 'up')}
              disabled={docIndex === 0 || docsTotal <= 1}
              className="p-0.5 rounded text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              aria-label="Monter ce fichier"
            >
              <ChevronUp className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => onMoveDoc(doc.id, 'down')}
              disabled={docIndex === docsTotal - 1 || docsTotal <= 1}
              className="p-0.5 rounded text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              aria-label="Descendre ce fichier"
            >
              <ChevronDown className="w-3 h-3" />
            </button>
          </div>

          <div className="flex items-center gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
            {/* Rename */}
            <button
              type="button"
              onClick={() => setIsRenaming(true)}
              className="p-1 rounded text-neutral-400 hover:text-primary-600 hover:bg-primary-50 transition-all"
              aria-label="Renommer ce fichier"
              title="Renommer"
            >
              <Pencil className="w-3 h-3" />
            </button>

            {/* Move to another category — portal-positioned to avoid overflow clipping */}
            {reclassifyTargets.length > 0 && (
              <>
                <button
                  ref={moveButtonRef}
                  type="button"
                  onClick={() => setMoveOpen((v) => !v)}
                  className={`p-1 rounded transition-all ${
                    moveOpen
                      ? 'text-primary-600 bg-primary-50'
                      : 'text-neutral-400 hover:text-primary-600 hover:bg-primary-50'
                  }`}
                  aria-label="Déplacer vers une autre catégorie"
                  title="Déplacer vers"
                >
                  <FolderInput className="w-3 h-3" />
                </button>
                {moveOpen && (
                  <MovePopover
                    doc={doc}
                    availableFields={availableFields}
                    anchorEl={moveButtonRef.current}
                    onReclassify={onReclassify}
                    onClose={() => setMoveOpen(false)}
                  />
                )}
              </>
            )}

            {/* Preview */}
            <button
              type="button"
              onClick={() => onPreviewDoc(doc)}
              className="p-1 rounded text-neutral-400 hover:text-primary-600 hover:bg-primary-50 transition-all"
              aria-label="Aperçu du fichier"
              title="Aperçu"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>

            {/* Exclude from dossier */}
            <button
              type="button"
              onClick={() => onExcludeDoc(doc.id)}
              className="p-1 rounded text-neutral-400 hover:text-error-500 hover:bg-error-50 transition-all"
              aria-label="Retirer ce fichier du dossier"
              title="Retirer du dossier"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
